import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from 'components/Button';
import ConfirmationModal from 'components/Modal/ConfirmationModal';
import Page from 'components/Page';
import { REDUCERS } from 'config/reducers';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteTranscript } from 'store/action/transcription';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { Heading } from '../Consultation/styles';

interface Transcript {
  id: string;
  consultationId: string;
  createdAt: string;
  redactedTranscription: string;
  userId: string;
  __v: number;
  _id: string;
}

const PastConsultations: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    transcription: { transcriptions },
  } = useAppState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<string | null>(null);

  const handleViewTranscript = async (transcript: Transcript) => {
    dispatch({
      type: REDUCERS.TRANSCRIPTION.ADD_TRANSCRIPTION,
      payload: transcript.redactedTranscription,
    });
    dispatch({
      type: REDUCERS.SUMMARY.SET_CONSULATION_ID,
      payload: { consultationId: transcript.consultationId },
    });
    navigate('/', { state: { transcript: [transcript.redactedTranscription] } });
  };

  const handleDelete = (id: string) => {
    setSelectedConsultation(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedConsultation) {
      await deleteTranscript(selectedConsultation, dispatch);
    }
    setShowDeleteModal(false);
    setSelectedConsultation(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedConsultation(null);
  };

  const handleBack = () => {
    dispatch({ type: REDUCERS.TRANSCRIPTION.RESET_TRANSCRIPTION });
    navigate('/', { replace: true, state: {} });
  };

  return (
    <Page>
      <div style={{ position: 'relative' }}>
        <Button
          id="back-button"
          buttonText="Back"
          onClick={handleBack}
          state="secondary"
          disabled={false}
          style={{ position: 'absolute', top: 0, right: 0 }}
        />
        <Heading>Past consultations</Heading>
        <p>
          Access all consultations conducted in the past 24 hours. They will be automatically deleted after this time
        </p>

        {transcriptions && transcriptions.length > 0 ? (
          <Paper style={{ marginTop: '2rem' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Transcript (preview)</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transcriptions.map((transcript: Transcript) => {
                  const date = new Date(transcript.createdAt);
                  const dateStr = date.toLocaleDateString();
                  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  return (
                    <TableRow key={transcript.id}>
                      <TableCell>{dateStr}</TableCell>
                      <TableCell>{timeStr}</TableCell>
                      <TableCell style={{ maxWidth: '200px' }}>
                        {transcript.redactedTranscription.slice(0, 150)} ...
                      </TableCell>
                      <TableCell style={{ width: '120px' }}>
                        <Button
                          id={`view-${transcript.id}`}
                          buttonText="View"
                          onClick={() => handleViewTranscript(transcript)}
                          state="primary"
                          disabled={false}
                          icon={<RemoveRedEyeIcon />}
                          iconPosition="left"
                          style={{ marginBottom: '8px' }}
                        />
                        <Button
                          id={`delete-${transcript.id}`}
                          buttonText="Delete"
                          onClick={() => handleDelete(transcript.id)}
                          state="secondary"
                          disabled={false}
                          icon={null}
                          iconPosition="left"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        ) : (
          <p style={{ marginTop: '2rem' }}>There are no transcriptions saved.</p>
        )}
        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Confirm Deletion"
          message="Are you sure you want to delete this consultation?"
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      </div>
    </Page>
  );
};

export default PastConsultations;
